// Login page. Submit-on-enter, auto-focus, full-width button on
// mobile. Errors render inline with a polite message — never the raw stack.
//
// Cuentas personales (docs/cuentas-personales.md): usuario + contraseña y después el código de Google
// Authenticator (o un código de recuperación). Mientras Pau no termine su alta sigue existiendo la contraseña
// compartida: entonces el campo de usuario sale solo si se pide («Entrar con mi cuenta»).
//
// Auth is held by the parent hook (useAuth) so the same instance can flip the
// global state and let the router redirect us to '/'.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiHttpError } from '../lib/api';
import { PRODUCT_NAME } from '../lib/branding';
import { LogoVenuz } from '../components/LogoVenuz';
import { useTranslation } from '../i18n';
import type { ResultadoLogin } from '../hooks/useAuth';

export interface LoginPageProps {
  authed: boolean;
  /** Con usuario devuelve el reto del segundo paso; sin él (contraseña compartida) entra directamente. */
  login: (password: string, usuario?: string) => Promise<ResultadoLogin | void>;
  loginCodigo?: (reto: string, codigo: string) => Promise<unknown>;
  /** false = ya no hay contraseña compartida: siempre se entra con usuario. */
  contrasenaCompartida?: boolean;
}

export function LoginPage({ authed, login, loginCodigo, contrasenaCompartida = true }: LoginPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conCuenta, setConCuenta] = useState(!contrasenaCompartida);
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [reto, setReto] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const usuarioRef = useRef<HTMLInputElement>(null);
  const codigoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!contrasenaCompartida) setConCuenta(true);
  }, [contrasenaCompartida]);

  useEffect(() => {
    if (authed) navigate('/', { replace: true });
  }, [authed, navigate]);

  useEffect(() => {
    if (reto) codigoRef.current?.focus();
    else if (conCuenta && !usuario) usuarioRef.current?.focus();
    else inputRef.current?.focus();
    // Solo al cambiar de paso: no robar el foco mientras se escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reto, conCuenta]);

  async function onCodigo(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reto || !loginCodigo) return;
    if (!codigo.trim()) {
      setError(t('login.codeEmpty'));
      return;
    }
    setSubmitting(true);
    try {
      await loginCodigo(reto, codigo.trim());
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiHttpError && err.code === 'RETO_CADUCADO') {
        setReto(null);
        setPassword('');
      }
      setError(err instanceof ApiHttpError ? err.message : t('login.couldNotSignIn'));
      setCodigo('');
      codigoRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password) {
      setError(t('login.passwordEmpty'));
      return;
    }
    if (conCuenta && !usuario.trim()) {
      setError(t('login.userEmpty'));
      return;
    }
    setSubmitting(true);
    try {
      const r = conCuenta ? await login(password, usuario.trim()) : await login(password);
      if (r && r.segundoPaso) {
        setReto(r.reto);
        setCodigo('');
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiHttpError ? err.message : t('login.couldNotSignIn');
      setError(msg);
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex min-h-full items-center justify-center bg-background px-4 py-safe-top pb-safe-bottom"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-7 shadow-xl">
        {/* Sprint 5.7 (D28) — single-user product; the marketing subtitle
            ("Sign in to continue." / "Manage your AI coding agents.") was
            removed as pure noise. Only the product wordmark + the sign-in
            form remain. */}
        <h1 className="mb-7 text-foreground">
          <LogoVenuz className="h-8 w-auto" titulo={PRODUCT_NAME} />
          <span className="mt-3 block text-body font-normal text-muted-foreground">{t('oficinaV2.titulo')}</span>
        </h1>

        {reto ? (
          <form onSubmit={onCodigo} noValidate data-testid="login-codigo-form">
            <label htmlFor="codigo" className="block text-body font-medium text-foreground mb-2">
              {t('login.codeLabel')}
            </label>
            <input
              id="codigo"
              ref={codigoRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codigo}
              maxLength={20}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={submitting}
              className="w-full bg-background text-foreground tracking-widest rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              data-testid="codigo-input"
            />
            <p className="mt-2 text-caption text-muted-foreground">{t('login.codeHelp')}</p>
            {error ? (
              <p role="alert" className="mt-3 text-body text-status-error" data-testid="login-error">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full bg-action hover:bg-action/90 disabled:bg-popover text-action-foreground font-medium rounded-lg px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-white/85 focus:ring-offset-2 focus:ring-offset-card"
              data-testid="codigo-submit"
            >
              {submitting ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>
        ) : (
        <form onSubmit={onSubmit} noValidate>
          {conCuenta ? (
            <>
              <label htmlFor="usuario" className="block text-body font-medium text-foreground mb-2">
                {t('login.userLabel')}
              </label>
              <input
                id="usuario"
                ref={usuarioRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                disabled={submitting}
                className="mb-4 w-full bg-background text-foreground rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                data-testid="usuario-input"
              />
            </>
          ) : null}
          <label
            htmlFor="password"
            className="block text-body font-medium text-foreground mb-2"
          >
            {t('login.passwordLabel')}
          </label>
          <input
            id="password"
            ref={inputRef}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            className="w-full bg-background text-foreground placeholder:text-muted-foreground/70 rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            data-testid="password-input"
          />

          {error ? (
            <p
              role="alert"
              className="mt-3 text-body text-status-error"
              data-testid="login-error"
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="mt-5 w-full bg-action hover:bg-action/90 disabled:bg-popover text-action-foreground font-medium rounded-lg px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-white/85 focus:ring-offset-2 focus:ring-offset-card"
            data-testid="login-submit"
          >
            {submitting ? t('login.signingIn') : t('login.signIn')}
          </button>
          {contrasenaCompartida && !conCuenta ? (
            <button
              type="button"
              onClick={() => setConCuenta(true)}
              className="mt-4 w-full text-body text-muted-foreground underline underline-offset-2 hover:text-foreground"
              data-testid="login-con-cuenta"
            >
              {t('login.withAccount')}
            </button>
          ) : null}
        </form>
        )}
      </div>
    </div>
  );
}
